import { useState, useMemo } from 'react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { TrialBalance } from './reports/TrialBalance';
import { ProfitAndLoss } from './reports/ProfitAndLoss';
import { BalanceSheet } from './reports/BalanceSheet';
import { TaxSummary } from './reports/TaxSummary';
import { CashFlow } from './reports/CashFlow';
import { LedgerMasterReport } from './reports/LedgerMasterReport';
import { Gstr1SummaryReport } from './reports/Gstr1SummaryReport';
import { Gstr2bReportComponent } from './reports/Gstr2bReportComponent';
import { Gstr3bReportComponent } from './reports/Gstr3bReportComponent';
import { Input } from '@/shared/components/ui/Input';
import {
  BarChart3,
  Search,
  Layers,
  Sparkles,
  Receipt,
  Scale,
} from 'lucide-react';
import { localToday } from '@/shared/utils/utils';

type ReportType =
  | 'ledger-master'
  | 'trial-balance'
  | 'profit-and-loss'
  | 'balance-sheet'
  | 'cash-flow'
  | 'tax-summary'
  | 'gstr-1'
  | 'gstr-2b'
  | 'gstr-3b';

interface ReportTabOption {
  id: ReportType;
  label: string;
  category: 'Core' | 'Tax & GST';
  icon: any;
  description: string;
}

const REPORT_TABS: ReportTabOption[] = [
  { id: 'ledger-master', label: 'All Ledger Master', category: 'Core', icon: Layers, description: 'Complete chart of accounts master balances' },
  { id: 'trial-balance', label: 'Trial Balance', category: 'Core', icon: Scale, description: 'Debit & credit ledger balance audit' },
  { id: 'profit-and-loss', label: 'Profit & Loss', category: 'Core', icon: BarChart3, description: 'Operating revenue, expense & net profit' },
  { id: 'balance-sheet', label: 'Balance Sheet', category: 'Core', icon: Scale, description: 'Financial position: Assets = Liabilities + Equity' },
  { id: 'cash-flow', label: 'Cash Flow', category: 'Core', icon: Receipt, description: 'Indirect method operating, investing & financing flow' },
  { id: 'tax-summary', label: 'Tax Summary', category: 'Tax & GST', icon: Sparkles, description: 'GST & TDS input credit vs output tax' },
  { id: 'gstr-1', label: 'GSTR-1 Summary', category: 'Tax & GST', icon: Sparkles, description: 'Statutory outward supplies return' },
  { id: 'gstr-2b', label: 'GSTR-2B ITC', category: 'Tax & GST', icon: Sparkles, description: 'Auto-drafted ITC statement matching' },
  { id: 'gstr-3b', label: 'GSTR-3B Return', category: 'Tax & GST', icon: Sparkles, description: 'Monthly summary tax payment return' },
];

export function ReportsTab() {
  const [activeReport, setActiveReport] = useState<ReportType>('ledger-master');
  const [startDate, setStartDate] = useState(localToday(new Date(new Date().getFullYear(), 3, 1))); // April 1st
  const [endDate, setEndDate] = useState(localToday());
  const [tabSearch, setTabSearch] = useState('');

  const filteredTabs = useMemo(() => {
    if (!tabSearch.trim()) return REPORT_TABS;
    const q = tabSearch.toLowerCase();
    return REPORT_TABS.filter((t) => t.label.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
  }, [tabSearch]);

  const activeTabInfo = REPORT_TABS.find((t) => t.id === activeReport) || REPORT_TABS[0];

  return (
    <div className="h-full flex flex-col space-y-6 pb-6">
      {/* ── Sub-Navigation Controls Header ──────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-950 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <span>Financial Statements & Tax Reporting</span>
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Select a financial report statement to run live calculations from posted journal entries.
            </p>
          </div>

          {/* Date Picker Controls & Presets */}
          <div className="flex flex-wrap items-center gap-3 bg-slate-50 dark:bg-slate-900 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">From</span>
              <Input
                type="date"
                className="h-8 text-xs w-36 bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 rounded-lg shadow-2xs font-mono"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">To</span>
              <Input
                type="date"
                className="h-8 text-xs w-36 bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 rounded-lg shadow-2xs font-mono"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* Quick FY buttons */}
            <div className="flex items-center gap-1 pl-2 border-l border-slate-200 dark:border-slate-800">
              <button
                onClick={() => {
                  setStartDate('2026-04-01');
                  setEndDate('2027-03-31');
                }}
                className="px-2 py-1 text-[11px] font-semibold bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Current FY
              </button>
            </div>
          </div>
        </div>

        {/* Report Selector Pills Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2 border-t border-slate-100 dark:border-slate-900">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
            {filteredTabs.map((r) => {
              const Icon = r.icon;
              const isActive = activeReport === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => setActiveReport(r.id)}
                  className={`flex items-center gap-2 px-3.5 py-2 text-xs rounded-xl font-bold transition-all shrink-0 ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                      : 'bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{r.label}</span>
                </button>
              );
            })}
          </div>

          <div className="relative shrink-0">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search statements..."
              value={tabSearch}
              onChange={(e) => setTabSearch(e.target.value)}
              className="w-full sm:w-44 pl-8 pr-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
            />
          </div>
        </div>
      </div>

      {/* ── Active Report View Wrapper ────────────────────────────────────── */}
      <Card className="border border-slate-200 dark:border-slate-800 shadow-sm rounded-2xl overflow-hidden flex-1 flex flex-col">
        <CardHeader
          className="bg-slate-50/70 dark:bg-slate-900/60 py-4 px-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between"
          title={
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-indigo-600/10 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400 flex items-center justify-center font-bold">
                <activeTabInfo.icon className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                  {activeReport === 'ledger-master' && 'All Ledger Master Report'}
                  {activeReport === 'trial-balance' && 'Trial Balance Statement'}
                  {activeReport === 'profit-and-loss' && 'Profit & Loss Statement (Income Statement)'}
                  {activeReport === 'balance-sheet' && `Balance Sheet Statement (As of ${new Date(endDate).toLocaleDateString('en-IN')})`}
                  {activeReport === 'cash-flow' && 'Cash Flow Statement (Indirect Method)'}
                  {activeReport === 'tax-summary' && 'Tax Summary (GST & TDS Payable / Receivable)'}
                  {activeReport === 'gstr-1' && 'GSTR-1 Outward Supplies Summary'}
                  {activeReport === 'gstr-2b' && 'GSTR-2B Auto ITC Statement'}
                  {activeReport === 'gstr-3b' && 'GSTR-3B Self-Declaration Summary'}
                </h3>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  {activeTabInfo.description} • Filtered from <strong className="text-slate-700 dark:text-slate-300 font-mono">{startDate}</strong> to <strong className="text-slate-700 dark:text-slate-300 font-mono">{endDate}</strong>
                </p>
              </div>
            </div>
          }
        />
        <CardBody className="flex-1 overflow-auto p-6 bg-slate-50/30 dark:bg-slate-900/20">
          <div className="max-w-6xl mx-auto space-y-4">
            {activeReport === 'ledger-master' && <LedgerMasterReport />}
            {activeReport === 'trial-balance' && <TrialBalance startDate={startDate} endDate={endDate} />}
            {activeReport === 'profit-and-loss' && <ProfitAndLoss startDate={startDate} endDate={endDate} />}
            {activeReport === 'balance-sheet' && <BalanceSheet asOfDate={endDate} />}
            {activeReport === 'cash-flow' && <CashFlow startDate={startDate} endDate={endDate} />}
            {activeReport === 'tax-summary' && <TaxSummary startDate={startDate} endDate={endDate} />}
            {activeReport === 'gstr-1' && <Gstr1SummaryReport />}
            {activeReport === 'gstr-2b' && <Gstr2bReportComponent />}
            {activeReport === 'gstr-3b' && <Gstr3bReportComponent />}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
